import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, DatePicker, Input, Modal, Row, Select, Space, Statistic, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTransaction } from './TransactionStore';
import { MainTable } from '../../Components/MainTable/MainTable';
import Add from './Add';
import Edit from './Edit';
import Transactions from './Transactions';

function AllTransactions() {
    const [open, setOpen] = useState(false);
    const { transactions, fetchTransactions, deleteTransaction, setEd, getKontrTr, updateTransactionStatus } = useTransaction();
    const [q, setQ] = useState('');
    const [type, setType] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [dateRange, setDateRange] = useState(null);

    useEffect(() => {
        fetchTransactions()
    }, [fetchTransactions])

    const currencyFmt = useMemo(() => new Intl.NumberFormat('uz-UZ'), []);

    const statusMeta = useMemo(() => {
        return {
            pending: { label: 'Jarayonda', color: 'gold' },
            completed: { label: 'Bajarilgan', color: 'green' },
            failed: { label: 'Bajarilmagan', color: 'red' },
        }
    }, [])

    const currencyMeta = useMemo(() => {
        return {
            UZS: { color: 'blue' },
            USD: { color: 'geekblue' },
            EUR: { color: 'purple' },
            RUB: { color: 'cyan' },
        }
    }, [])

    const filtered = useMemo(() => {
        const list = Array.isArray(transactions) ? transactions : [];

        return list.filter((t) => {
            const fullName = `${t?.first_name ?? ''} ${t?.last_name ?? ''}`.trim().toLowerCase();
            const desc = `${t?.description ?? ''}`.toLowerCase();
            const queryOk = !q || fullName.includes(q.toLowerCase()) || desc.includes(q.toLowerCase());
            const typeOk = !type || t?.transaction_type === type;
            const statusOk = !status || t?.status === status;

            let dateOk = true;
            if (dateRange?.[0] && dateRange?.[1]) {
                const d = t?.transaction_date || t?.created_at;
                if (!d) {
                    dateOk = false;
                } else {
                    const td = dayjs(d);
                    dateOk = td.isValid() && td.isAfter(dateRange[0].startOf('day')) && td.isBefore(dateRange[1].endOf('day'));
                }
            }

            return queryOk && typeOk && statusOk && dateOk;
        });
    }, [transactions, q, type, status, dateRange]);

    const stats = useMemo(() => {
        const list = filtered;
        const credit = list.reduce((a, t) => a + (t?.transaction_type === 'credit' ? Number(t?.amount ?? 0) : 0), 0);
        const debit = list.reduce((a, t) => a + (t?.transaction_type === 'debit' ? Number(t?.amount ?? 0) : 0), 0);
        const pending = list.filter((t) => t?.status === 'pending').length;
        const completed = list.filter((t) => t?.status === 'completed').length;
        const failed = list.filter((t) => t?.status === 'failed').length;
        return { credit, debit, balance: credit - debit, pending, completed, failed, total: list.length };
    }, [filtered]);

    const columns = useMemo(() => {
        return [
            {
                title: 'Kontragent',
                dataIndex: 'first_name',
                key: 'kontragent',
                render: (_, record) => (
                    <div>
                        <div style={{ fontWeight: 600 }}>{`${record?.first_name ?? ''} ${record?.last_name ?? ''}`.trim() || '-'}</div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{record?.customer_id ? `ID: ${record.customer_id}` : ''}</div>
                    </div>
                )
            },
            {
                title: 'Turi',
                dataIndex: 'transaction_type',
                key: 'transaction_type',
                width: 110,
                render: (val) => {
                    const isDebit = val === 'debit';
                    return <Tag color={isDebit ? 'volcano' : 'green'}>{isDebit ? 'Chiqim' : 'Kirim'}</Tag>;
                }
            },
            {
                title: 'Summa',
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                width: 160,
                render: (val, record) => {
                    const isDebit = record?.transaction_type === 'debit';
                    const n = Number(val ?? 0);
                    const sign = isDebit ? '-' : '+';
                    const curr = (record?.currency || 'UZS').toUpperCase();
                    return (
                        <Tag color={isDebit ? 'volcano' : 'green'} style={{ marginInlineEnd: 0, fontWeight: 600 }}>
                            {sign}{currencyFmt.format(n)} {curr}
                        </Tag>
                    );
                }
            },
            {
                title: 'Valyuta',
                dataIndex: 'currency',
                key: 'currency',
                width: 90,
                render: (v) => {
                    const curr = (v || 'UZS').toUpperCase();
                    const meta = currencyMeta[curr] || { color: 'default' };
                    return <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>{curr}</Tag>;
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 190,
                render: (val, record) => (
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Tag
                            color={(statusMeta[val]?.color) || 'default'}
                            style={{ marginInlineEnd: 0, width: 'fit-content' }}
                        >
                            {(statusMeta[val]?.label) || val || '-'}
                        </Tag>
                        <Select
                            value={val}
                            style={{ width: '100%' }}
                            onChange={(value) => updateTransactionStatus({ ...record, status: value })}
                            options={Object.entries(statusMeta).map(([value, meta]) => ({ value, label: meta.label }))}
                        />
                    </Space>
                )
            },
            {
                title: 'Sana',
                dataIndex: 'transaction_date',
                key: 'transaction_date',
                width: 160,
                render: (_, record) => {
                    const d = record?.transaction_date || record?.created_at;
                    const dt = d ? dayjs(d) : null;
                    return dt && dt.isValid() ? dt.format('YYYY-MM-DD HH:mm') : '-';
                }
            },
            {
                title: 'Izoh',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
                render: (v) => v || '-',
            },
        ]
    }, [currencyFmt, currencyMeta, statusMeta, updateTransactionStatus])

    return (
        <>
            <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={'80%'}>
                <Transactions />
            </Modal>

            <div style={{ padding: 12 }}>
                <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12}>
                        <Typography.Title level={4} style={{ margin: 0 }}>Barcha Tranzaksiyalar</Typography.Title>
                        <Typography.Text type="secondary">Kirim/chiqimlar, status va tarix</Typography.Text>
                    </Col>
                    <Col xs={24} md={12}>
                        <Row gutter={[8, 8]} justify="end">
                            <Col xs={24} sm={12} md={10}>
                                <Input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Kontragent yoki izoh bo'yicha qidirish"
                                    allowClear
                                />
                            </Col>
                            <Col xs={12} sm={6} md={7}>
                                <Select
                                    value={type}
                                    onChange={setType}
                                    allowClear
                                    placeholder="Turi"
                                    style={{ width: '100%' }}
                                    options={[
                                        { value: 'credit', label: 'Kirim' },
                                        { value: 'debit', label: 'Chiqim' },
                                    ]}
                                />
                            </Col>
                            <Col xs={12} sm={6} md={7}>
                                <Select
                                    value={status}
                                    onChange={setStatus}
                                    allowClear
                                    placeholder="Status"
                                    style={{ width: '100%' }}
                                    options={Object.entries(statusMeta).map(([value, meta]) => ({ value, label: meta.label }))}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={10}>
                                <DatePicker.RangePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={14}>
                                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Typography.Text type="secondary">Natija:</Typography.Text>
                                    <Tag color="blue">{stats.total} ta</Tag>
                                </Space>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic title="Kirim" value={stats.credit} formatter={(v) => currencyFmt.format(Number(v || 0))} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic title="Chiqim" value={stats.debit} formatter={(v) => currencyFmt.format(Number(v || 0))} />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Statistic
                                title="Balans"
                                value={stats.balance}
                                valueStyle={{ color: stats.balance >= 0 ? '#389e0d' : '#cf1322' }}
                                formatter={(v) => currencyFmt.format(Number(v || 0))}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card size="small">
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography.Text type="secondary">Jarayonda</Typography.Text>
                                    <Tag color="gold">{stats.pending}</Tag>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography.Text type="secondary">Bajarilgan</Typography.Text>
                                    <Tag color="green">{stats.completed}</Tag>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography.Text type="secondary">Bajarilmagan</Typography.Text>
                                    <Tag color="red">{stats.failed}</Tag>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </div>

            <MainTable
                pageTitle={'Barcha Tranzaksiyalar'}
                pdata={filtered}
                pcolumns={columns}
                defkey={'id'}
                adw={1000}
                edw={1000}
                xwidth={1000}
                add={<Add />}
                edit={<Edit />}
                onDelete={deleteTransaction}
                onEdit={(rec) => setEd(rec)}
                setEd={setEd}
                viewBtn={true}
                onView={(e, rec) => {
                    getKontrTr(rec.id);
                    setOpen(true);
                }}
            />
        </>
    );
}
export default AllTransactions;